import { useState } from "react";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  lowercaseRegex,
  passwordRegex,
  specialCharRegex,
  uppercaseRegex,
} from "../../../utils/Constant";
import Loading from "../../../components/loading/loading";
import { resetCurrentUserPasswordRequest } from "../../../utils/APICalls";
import ErrorNotification from "../../../components/error-notification/error-notification";

type props = {
  showDialog: boolean;
  closeDialog: any;
  onResetSuccess?: any;
};

interface FormValues {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const ValidationSchema = yup.object({
  currentPassword: yup.string().required("This field can't be empty"),
  newPassword: yup
    .string()
    .required("This field can't be empty")
    .matches(passwordRegex, "Error"),
  confirmPassword: yup.string().required("This field can't be empty"),
});

export default function ResetPassword({
  showDialog,
  closeDialog,
  onResetSuccess,
}: props) {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lengthError, setLengthError] = useState(true);
  const [lowercaseError, setLowercaseError] = useState(true);
  const [uppercaseError, setUppercaseError] = useState(true);
  const [specialCharError, setSpecialCharError] = useState(true);
  const [confirmPasswordError, setConfirmPasswordError] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (value) => {
      const payload = {
        currentPassword: value.currentPassword,
        newPassword: value.newPassword,
      };
      resetPassword(payload);
    },
  });

  const onNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);

    const password = event.target.value;
    if (password.length >= 8 && password.length <= 16) {
      setLengthError(false);
    } else {
      setLengthError(true);
    }

    if (lowercaseRegex.test(password)) {
      setLowercaseError(false);
    } else {
      setLowercaseError(true);
    }

    if (uppercaseRegex.test(password)) {
      setUppercaseError(false);
    } else {
      setUppercaseError(true);
    }

    if (specialCharRegex.test(password)) {
      setSpecialCharError(false);
    } else {
      setSpecialCharError(true);
    }

    if (password === formik.values.confirmPassword) {
      setConfirmPasswordError(false);
    } else {
      setConfirmPasswordError(true);
    }
  };

  const onConfirmPasswordChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.handleChange(event);

    const password = event.target.value;
    if (password === formik.values.newPassword) {
      setConfirmPasswordError(false);
    } else {
      setConfirmPasswordError(true);
    }
  };

  const resetPassword = async (payload: any) => {
    setShowLoading(true);
    const resetPasswordResponse = await resetCurrentUserPasswordRequest(
      payload
    );
    setShowLoading(false);
    if (resetPasswordResponse?.status === 204) {
      onResetSuccess();
      closeDialog();
    } else if (resetPasswordResponse?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(resetPasswordResponse?.data?.detail);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ fontWeight: "510" }}>
        Reset Password?
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={(theme) => ({
          position: "absolute",
          right: 12,
          top: 12,
          color: "rgba(0, 0, 0, 0.54)",
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ width: 500, mt: -2 }}>
        <form onSubmit={formik.handleSubmit}>
          <FormControl sx={{ mt: 1, width: 500 }} variant="outlined">
            <InputLabel htmlFor="currentPassword">Current Password</InputLabel>
            <OutlinedInput
              id="currentPassword"
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showCurrentPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showCurrentPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Current Password"
              value={formik.values.currentPassword}
              onChange={formik.handleChange}
              error={
                formik.touched.currentPassword &&
                Boolean(formik.errors.currentPassword)
              }
            />
          </FormControl>

          <FormControl sx={{ mt: 3, width: 500 }} variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
              New Password
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showNewPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showNewPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="New Password"
              value={formik.values.newPassword}
              onChange={onNewPasswordChange}
              error={
                formik.touched.newPassword && Boolean(formik.errors.newPassword)
              }
            />
            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                ml: lengthError ? 3.5 : 0,
              }}
              color={lengthError ? "error" : "success"}
            >
              {" "}
              {!lengthError && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}{" "}
              8-16 characters
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                ml: lowercaseError ? 3.5 : 0,
              }}
              color={lowercaseError ? "error" : "success"}
            >
              {" "}
              {!lowercaseError && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}{" "}
              At least 1 lowercase
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                ml: uppercaseError ? 3.5 : 0,
              }}
              color={uppercaseError ? "error" : "success"}
            >
              {" "}
              {!uppercaseError && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}{" "}
              At least 1 uppercase
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                ml: specialCharError ? 3.5 : 0,
              }}
              color={specialCharError ? "error" : "success"}
            >
              {" "}
              {!specialCharError && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}{" "}
              Special character: ! @ # $ % ^ & *{" "}
            </Typography>
          </FormControl>

          <FormControl sx={{ mt: 3, width: 500 }} variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
              Confirm Password
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm Password"
              value={formik.values.confirmPassword}
              onChange={onConfirmPasswordChange}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
            />
            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                ml: confirmPasswordError ? 3.5 : 0,
              }}
              color={confirmPasswordError ? "error" : "success"}
            >
              {!confirmPasswordError && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}{" "}
              Passwords Must Match
            </Typography>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          sx={{ mr: 1, mb: 0.5 }}
          onClick={closeDialog}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ mr: 1.5, mb: 0.5 }}
          onClick={() => formik.handleSubmit()}
          autoFocus
          disabled={formik.isValid && !confirmPasswordError ? false : true}
        >
          Reset Password
        </Button>
      </DialogActions>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
      {showLoading && <Loading />}
    </Dialog>
  );
}
